/**
 * Central pricing logic. ONE place, pure functions, so the money math is
 * auditable and cannot drift between screens.
 *
 * THE MODEL:
 *   baseCost   = SUM over the team of (points x that person's rate)
 *   buyerPrice = baseCost x markup, rounded up to the nearest rupee
 *   payout     = each person's earning, less the 10% collaborator commission
 *
 * The platform earns on BOTH sides: the markup from the buyer, and the 10%
 * from each collaborator at payout. The markup is admin-configurable and is
 * FROZEN onto every quote it produces — changing it later must never
 * re-price a quote a buyer has already seen.
 *
 * Every value here is INTEGER PAISE. Points are integers.
 */

import {
  Money, Paise,
  MARKUP_SCALE, DEFAULT_BUYER_MARKUP, clampMarkup,
  PLATFORM_COMMISSION_NUMERATOR, PLATFORM_COMMISSION_DENOMINATOR,
  splitPayout, earningPaise as computeEarningPaise,
} from './money.model';
import { CollaboratorRole } from './collaborator.model';

export const PLATFORM_COMMISSION_RATE =
  PLATFORM_COMMISSION_NUMERATOR / PLATFORM_COMMISSION_DENOMINATOR;

/** Buyer price is rounded to whole rupees. Collaborator payouts are NEVER
 *  rounded to this granularity — a rounding choice made for the look of a
 *  price tag must not be absorbed by the person waiting on the money. */
export const BUYER_PRICE_ROUNDING_PAISE = 100;

/** Rounds UP, so the buyer price can never under-recover the base cost. */
export function roundBuyerPrice(paise: Paise): Paise {
  return Math.ceil(paise / BUYER_PRICE_ROUNDING_PAISE) * BUYER_PRICE_ROUNDING_PAISE;
}

export interface PointAllocation {
  collaboratorId: string;
  role: CollaboratorRole;
  displayName: string;
  headline?: string;
  points: number;
  ratePerPointPaise: Paise;
}

export interface CollaboratorPayout {
  collaboratorId: string;
  displayName: string;
  role: CollaboratorRole;
  points: number;
  earningPaise: Paise;
  payoutPaise: Paise;
  platformCutPaise: Paise;
}

export interface QuoteBreakdown {
  totalPoints: number;
  baseCostPaise: Paise;
  buyerPricePaise: Paise;
  /** The markup this quote was priced with, scaled by 10,000 (11000 = 1.1).
   *  Frozen here so the quote is reproducible even after the admin changes
   *  the platform default. */
  buyerMarkup: number;
  platformFromBuyerPaise: Paise;
  platformFromCollaboratorsPaise: Paise;
  totalPlatformMarginPaise: Paise;
  perCollaborator: CollaboratorPayout[];
}

export function computeQuote(
  allocations: PointAllocation[],
  buyerMarkup: number = DEFAULT_BUYER_MARKUP,
): QuoteBreakdown {
  const markup = clampMarkup(buyerMarkup);
  const active = allocations.filter((a) => a.points > 0 && a.ratePerPointPaise > 0);

  let baseCostPaise = 0;
  let totalPoints = 0;
  let platformFromCollaboratorsPaise = 0;
  const perCollaborator: CollaboratorPayout[] = [];

  for (const a of active) {
    const points = Math.round(a.points);
    const earning = computeEarningPaise(points, a.ratePerPointPaise);
    const { payoutPaise, platformCutPaise } = splitPayout(earning);

    baseCostPaise += earning;
    totalPoints += points;
    platformFromCollaboratorsPaise += platformCutPaise;

    perCollaborator.push({
      collaboratorId: a.collaboratorId,
      displayName: a.displayName,
      role: a.role,
      points,
      earningPaise: earning,
      payoutPaise,
      platformCutPaise,
    });
  }

  const rawBuyerPaise = baseCostPaise > 0
    ? Math.ceil((baseCostPaise * markup) / MARKUP_SCALE)
    : 0;

  const buyerPricePaise = roundBuyerPrice(rawBuyerPaise);
  const platformFromBuyerPaise = buyerPricePaise - baseCostPaise;

  return {
    totalPoints,
    baseCostPaise,
    buyerPricePaise,
    buyerMarkup: markup,
    platformFromBuyerPaise,
    platformFromCollaboratorsPaise,
    totalPlatformMarginPaise: platformFromBuyerPaise + platformFromCollaboratorsPaise,
    perCollaborator,
  };
}

export function buyerPriceMoney(breakdown: QuoteBreakdown): Money {
  return { amount: breakdown.buyerPricePaise, currency: 'INR' };
}